import type { AvoidType } from "@/api/adHocCommands"
import { Location } from "@/reducers/locationsSlice"
import { RouteStop } from "@/reducers/routes2gSlice"

export const stopKey = (stop: RouteStop, index: number): string =>
  stop.id ?? String(index)

export const indexOfStopKey = (stops: RouteStop[], key: string): number => {
  const index = stops.findIndex((stop, i) => stopKey(stop, i) === key)
  if (index < 0) throw new Error("The selected stop no longer exists")
  return index
}

export const moveStop = <T>(items: T[], from: number, to: number): T[] => {
  const moved = [...items]
  const [item] = moved.splice(from, 1)
  moved.splice(to, 0, item)
  return moved
}

export const reorderStops = (
  stops: RouteStop[],
  orderedKeys: string[],
): RouteStop[] => {
  const byKey = new Map(
    stops.map((stop, index) => [stopKey(stop, index), stop]),
  )
  const ordered = orderedKeys.flatMap((key) => byKey.get(key) ?? [])
  return [...ordered, ...stops.filter((stop) => !ordered.includes(stop))]
}

export const withStopLocation = (
  stop: RouteStop,
  location: Location,
): RouteStop => ({
  ...stop,
  location,
  msgId: undefined,
  addressUpdated: true,
})

export const withStopAction = (stop: RouteStop, action: string): RouteStop => ({
  ...stop,
  action,
})

export const newStop = (location: Location): RouteStop => ({ location })

const AVOID_TOLLS: AvoidType[] = ["tollRoads"]

export const withStopAvoidTolls = (
  stop: RouteStop,
  avoidTolls: boolean,
): RouteStop => ({ ...stop, avoid: avoidTolls ? AVOID_TOLLS : [] })

/** A stop's own avoid list, even an empty one, overrides the route's. */
export const stopAvoidsTolls = (
  stop: RouteStop,
  routeAvoid: AvoidType[] | undefined,
): boolean => (stop.avoid ?? routeAvoid ?? []).includes("tollRoads")
